import { Box, Grid, Text } from '@chakra-ui/react'
import { ContentNode, FieldContentNode, ObjectContentNode } from '@helo/cms'
import { Deferred, pipe } from '@helo/utils'

import { Skeleton } from '../components/SkeletonLoader'
import { WidgetRenderer2 } from '../widgets/WidgetRenderer/WidgetRenderer2'
import { IDRenderer } from './ID/IDRenderer'
import { StringRenderer } from './String/StringRenderer'
import { TypeRendererParts } from './TypeRenderer.parts'
import { DateRenderer } from './date/DateRenderer'
import { ListRenderer } from './list/ListRenderer'
import { NullRenderer } from './null/NullRenderer'
import { NumberRenderer } from './number/NumberRenderer'
import { PlaceRenderer } from './place/PlaceRenderer'
import { ServiceTypeRenderer } from './servicetype/ServiceTypeRenderer'
import { TagRenderer } from './tag/TagRenderer'

// GraphQL types from supergraph that have both tagName and tagValue fields and end with 'Tag'
const TAG_TYPE_NAMES = [
  'CatalogItemPurchaseOrderTag',
  'CatalogItemTag',
  'PurchaseOrderCiPoTag',
  'ShipmentDocumentCiPoTag',
  'ShipmentDocumentFulfillmentItemTag',
  'ShipmentDocumentFulfillmentItemOrderTag',
  'ShipmentDocumentTag',
  'TransportUnitCiPoTag',
  'TransportUnitClusterTag',
  'TransportUnitClusterCiPoTag',
  'TransportUnitShipmentDocumentTag',
]

export const TypeRenderer = ({
  contentNode,
  isLoading,
}: {
  contentNode: ContentNode
  isLoading?: boolean
}) => {
  if (!contentNode) {
    return <NullRenderer isLoading={isLoading} />
  }
  if (!contentNode) {
    console.error('no contentNode', { contentNode })
    return null
  }

  if (contentNode.state.asWidget) {
    const { asWidget, ...restState } = contentNode.state
    const contentNodeWithoutAsWidget = {
      ...contentNode,
      state: restState,
    }
    const widgetConfig = {
      ...(asWidget as any),
      state: {
        ...((asWidget as any).state || {}),
        widgetProps: {
          contentNode: contentNodeWithoutAsWidget,
          ...(restState.widgetOptions?.[asWidget?.type] || {}),
          ...((asWidget as any).state?.widgetProps || {}),
        },
      },
    }

    return (
      <WidgetRenderer2
        widgetConfig={widgetConfig}
        customPayload={{
          isLoading,
        }}
      />
    )
  }

  if (contentNode.kind === 'SCALAR') {
    if (contentNode.typeName === 'String') {
      return <StringRenderer contentNode={contentNode} isLoading={isLoading} />
    }
    if (contentNode.typeName === 'Int') {
      return <NumberRenderer contentNode={contentNode} isLoading={isLoading} />
    }
    if (contentNode.typeName === 'Float') {
      return <NumberRenderer contentNode={contentNode} isLoading={isLoading} />
    }
    if (contentNode.typeName === 'Boolean') {
      return <StringRenderer contentNode={contentNode} isLoading={isLoading} />
    }
    if (contentNode.typeName === 'ID') {
      return <IDRenderer contentNode={contentNode} isLoading={isLoading} />
    }
    if (
      contentNode.typeName === 'DateTime' ||
      contentNode.typeName === 'Date'
    ) {
      return <DateRenderer contentNode={contentNode} isLoading={isLoading} />
    }
  }

  if (TAG_TYPE_NAMES.includes(contentNode.typeName)) {
    return (
      <TagRenderer
        contentNode={contentNode as ObjectContentNode}
        isLoading={isLoading}
      />
    )
  }

  if (
    contentNode.typeName === 'Helo2Place' ||
    contentNode.typeName === 'Place'
  ) {
    return (
      <PlaceRenderer
        contentNode={contentNode as ObjectContentNode}
        isLoading={isLoading}
      />
    )
  }

  if (contentNode.typeName === 'Helo2ServiceType') {
    return (
      <ServiceTypeRenderer
        contentNode={contentNode as ObjectContentNode}
        isLoading={isLoading}
      />
    )
  }

  if (contentNode.kind === 'LIST') {
    return <ListRenderer contentNode={contentNode} isLoading={isLoading} />
  }

  if (contentNode.kind === 'OBJECT') {
    return <ObjectRenderer contentNode={contentNode} isLoading={isLoading} />
  }

  if (contentNode.kind === 'FIELD') {
    const ofTypeWithState = Deferred.merge({
      state: pipe(
        contentNode.state ?? {},
        Deferred.merge(contentNode.ofType.state ?? {}),
      ),
    })(contentNode.ofType)
    return <TypeRenderer contentNode={ofTypeWithState} isLoading={isLoading} />
  }

  console.error('unknown type', { contentNode })
  return '-'
}

const ObjectRenderer = ({
  contentNode,
  isLoading,
}: {
  contentNode: ObjectContentNode
  isLoading?: boolean
}) => {
  if (contentNode.kind !== 'OBJECT') {
    console.error('not an object', { contentNode })
    return null
  }

  const fields = contentNode.fields.map(fieldConfig => {
    return (
      <FieldRenderer
        key={fieldConfig.key}
        contentNode={fieldConfig}
        isLoading={isLoading}
      />
    )
  })

  return (
    <Box>
      {/* <Text fontWeight="bold" fontSize="30px">
        {contentNode.state.label as any}
      </Text> */}
      <Grid templateColumns="min-content 1fr" gap={1} rowGap={0} columnGap={1}>
        {fields}
      </Grid>
    </Box>
  )
}

const FieldRenderer = ({
  contentNode,
  isLoading,
}: {
  contentNode: FieldContentNode
  isLoading?: boolean
}) => {
  const isFieldPending = TypeRendererParts.isValuePending(
    contentNode.ofType,
    isLoading,
  )

  return (
    <>
      <Skeleton isLoading={isFieldPending} h="14px" w="56px" my="1px">
        <Text fontSize="14px" lineHeight="17px" fontWeight="500">
          {contentNode.state.label as string}:
        </Text>
      </Skeleton>

      <TypeRenderer
        contentNode={contentNode.ofType}
        isLoading={isFieldPending}
      />
    </>
  )
}
